import { defineStore } from "pinia";
import { openDB } from "idb";

export const useSessionStore = defineStore({
  id: "session",
  state: () => ({
    user: null,
    instructor: null,
    jwt: null,
    downloads: null,
    announcements: null,
  }),
  actions: {
    createObjectStores(db) {
      if (!db.objectStoreNames.contains("users")) {
        db.createObjectStore("users");
      }
      if (!db.objectStoreNames.contains("instructors")) {
        db.createObjectStore("instructors");
      }
      if (!db.objectStoreNames.contains("jwt")) {
        db.createObjectStore("jwt");
      }
      if (!db.objectStoreNames.contains("downloads")) {
        db.createObjectStore("downloads");
      }
    },

    createObjectAnnouncements(db) {
      if (!db.objectStoreNames.contains("announcements")) {
        db.createObjectStore("announcements");
      }
    },

    async initDB() {
      return await openDB("my-db", 3, {
        upgrade: (db) => {
          this.createObjectStores(db);
        },
      });
    },

    async initAnnouncementsDB() {
      // Initialize the IndexedDB database
      return openDB("AnnouncementsDB", 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains("announcements")) {
            db.createObjectStore("announcements", { keyPath: "id" }); // Keyed by announcement ID
          }
        },
      });
    },

    async setAnnouncements(announcements) {
      const db = await this.initAnnouncementsDB();
      const tx = db.transaction("announcements", "readwrite");
      const store = tx.objectStore("announcements");

      // Store announcements individually
      for (const announcement of announcements) {
        await store.put(announcement);
      }

      await tx.done;
    },

    async loadAnnouncements() {
      const db = await this.initAnnouncementsDB();
      const tx = db.transaction("announcements", "readonly");
      const store = tx.objectStore("announcements");

      // Retrieve all announcements from IndexedDB
      const allAnnouncements = await store.getAll();
      return allAnnouncements;
    },

    async setJWT(jwt) {
      this.jwt = jwt;
      // Save the jwt to IndexedDB
      const db = await this.initDB();
      await db.put("jwt", jwt, "token");
    },

    async loadJWT() {
      // Load the jwt from IndexedDB
      const db = await this.initDB();
      this.jwt = await db.get("jwt", "token");
      return this.jwt;
    },

    async clearJWT() {
      this.jwt = null;

      // Remove the jwt from IndexedDB
      const db = await this.initDB();
      await db.delete("jwt", "token");
    },

    async setDownloads(downloads) {
      this.downloads = downloads;

      // Save the downloads to IndexedDB
      const db = await this.initDB();
      await db.put("downloads", downloads, "current");
    },

    async loadDownloads() {
      // Load the jwt from IndexedDB
      const db = await this.initDB();
      this.downloads = await db.get("downloads", "current");
      return this.downloads;
    },

    async clearDownloads() {
      this.downloads = null;

      // Remove the jwt from IndexedDB
      const db = await this.initDB();
      await db.delete("downloads", "current");
    },

    async setUser(user) {
      this.user = user;

      // Save the user to IndexedDB
      const db = await this.initDB();
      await db.put("users", user, "current");
    },

    async loadUser() {
      // Load the user from IndexedDB
      const db = await this.initDB();
      this.user = await db.get("users", "current");
      return this.user;
    },

    async clearUser() {
      this.user = null;

      // Remove the user from IndexedDB
      const db = await this.initDB();
      await db.delete("users", "current");
    },

    async setInstructor(instructor) {
      this.instructor = instructor;

      // Save the instructor to IndexedDB
      const db = await this.initDB();
      await db.put("instructors", instructor, "current");
    },

    async loadInstructor() {
      // Load the instructor from IndexedDB
      const db = await this.initDB();
      this.instructor = await db.get("instructors", "current");
      return this.instructor;
    },

    async clearInstructor() {
      this.instructor = null;

      // Remove the instructor from IndexedDB
      const db = await this.initDB();
      await db.delete("instructors", "current");
    },
  },
});
